import { Button } from "@bfc-software/pallet";
import { TFunction } from "i18next";
import { isDefined } from "@bfc-software/app-toolbox";

import styles from "./cursor-pager.module.css";

/** The `Pagination` envelope every task planner list response carries. */
type PaginationState = { has_more: boolean; cursor?: string | null };

/**
 * Forward/back controls for a cursor-paginated list.
 *
 * Only Next actually works. A response carries the cursor for the next page and
 * nothing pointing backwards, so Previous is rendered permanently disabled --
 * present because the control belongs in the row, inert because the API cannot
 * yet answer it. It lights up when the backend adds `prev_cursor`, or becomes
 * moot if offset paging with a total lands instead (at which point this whole
 * component gives way to pallet's `Pagination`, which needs `totalPages`).
 *
 * For the same reason there is no page indicator: page number is only knowable
 * by counting cursors the client has burned through, which is bookkeeping the
 * URL should not be carrying.
 */
export function CursorPager({
  pagination,
  isFetching,
  onChange,
  t,
}: {
  pagination?: PaginationState;
  isFetching: boolean;
  // Only ever called with the next page's cursor. Nothing clears it back to
  // page 1 while Previous is inert.
  onChange: (cursor: string) => void;
  t: TFunction;
}) {
  // `cursor` is documented NULL whenever has_more is false. Reading the cursor
  // itself rather than the flag means a response that disagrees with itself
  // can never produce a Next button that navigates nowhere.
  const nextCursor =
    pagination?.has_more === true ? pagination.cursor : undefined;
  const canGoForward = isDefined(nextCursor);

  // Rendered unconditionally, disabled included: the row is a fixed part of the
  // table chrome, so the table does not shift vertically when a result set
  // happens to spill onto a second page.
  return (
    <nav
      className={styles.pager}
      aria-label={t("pagination.ariaLabel")}
      data-test-role="cursor-pager"
    >
      <div className={styles.controls}>
        <Button
          variant="tertiary"
          icon={{ type: "chevron-left" }}
          text={t("pagination.previous")}
          // Never enabled today. Not wired to browser history either: that
          // retraces correctly on a normal forward walk but walks straight out
          // of the app when someone opens a shared link already carrying a
          // cursor, and the two cases are indistinguishable from here.
          isDisabled={true}
          data-test-role="cursor-pager-previous"
        />
        <Button
          variant="tertiary"
          icon={{ type: "chevron-right", align: "right" }}
          text={t("pagination.next")}
          isDisabled={!canGoForward || isFetching}
          onPress={() => isDefined(nextCursor) && onChange(nextCursor)}
          data-test-role="cursor-pager-next"
        />
      </div>
    </nav>
  );
}
